import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import { Router, Location, Redirect } from '@reach/router';
import {
  ThemeProvider as FreemiumThemeProvider,
  UiProvider as FreemiumUiProvider
} from 'freemium-ui';
import { ToastContainer } from 'react-toastify';
import { ReactQueryConfigProvider } from 'react-query';
import ErrorBoundary from 'react-error-boundary';
import loadable from 'react-loadable';
// import { ReactQueryDevtools } from 'react-query-devtools'
import { ThemeProvider } from 'styled-components';
import appConfig from 'api/config';
import { AppProvider, AppConsumer } from 'shared/Application/app-context';
import { IsolatedContainer } from './shared/pattern';
import IncidentsPage from './screens/incidents';
import StatusPageCustomization from './screens/statuspage-customize';
import SettingsPage from './screens/settings';
import ReportsPage from './screens/reports';
import theme from './shared/theme';
import Application, { LoadingFallback } from './shared/Application';
import './i18n';

// import fstatustheme from './shared/theme/fstatustheme';
// import theme from './shared/default'

import GlobalResetCSS from './reset.css';
// import * as serviceWorker from './serviceWorker';

type PosedRouterProps = {
  children: any
};

const PosedRouter = ({ children }: PosedRouterProps) => (
  <Location>
    {({ location }) => (
      <Router key={location.pathname} location={location}>
        {children}
      </Router>
    )}
  </Location>
);
/**
 * Alerts routes
 */

const AlertsPage = loadable({
  loader: () => import('./screens/alerts'),
  loading: LoadingFallback
});
const ExplorePage = loadable({
  loader: () => import('./screens/explore'),
  loading: LoadingFallback
});

/**
 * Incidents sub routes
 */
const IncidentsList = loadable({
  loader: () => import('./screens/incidents/screens/IncidentList'),
  loading: LoadingFallback
});
const IncidentView = loadable({
  loader: () => import('./screens/incidents/screens/IncidentView'),
  loading: LoadingFallback
});
const IncidentAction = loadable({
  loader: () => import('./screens/incidents/screens/IncidentAction'),
  loading: LoadingFallback
});
// const AnnouncementList = loadable({
//   loader: () => import('./screens/incidents/screens/AnnouncementList'),
//   loading: LoadingFallback
// });

const QuickStatusChange = loadable({
  loader: () => import('./screens/incidents/screens/StatusChange'),
  loading: LoadingFallback
});

/**
 * Freshid sub routes
 */
const FreshidSignupSuccess = loadable({
  loader: () => import('./screens/freshid/FreshidSignupSuccess'),
  loading: LoadingFallback
});

const FreshidSSOSuccess = loadable({
  loader: () => import('./screens/freshid/FreshidSSOSuccess'),
  loading: LoadingFallback
});

const FreshidLogout = loadable({
  loader: () => import('./screens/freshid/FreshidLogout'),
  loading: LoadingFallback
});

const FreshstatusLogout = loadable({
  loader: () => import('./screens/freshid/FreshstatusLogout'),
  loading: LoadingFallback
});

const FreshidLogin = loadable({
  loader: () => import('./screens/freshid/FreshidLogin'),
  loading: LoadingFallback
});

/**
 * Service customization
 */
const StatusEmbed = loadable({
  loader: () => import('./screens/status-embed'),
  loading: LoadingFallback
});
const StatusBadgeView = loadable({
  loader: () => import('./screens/status-embed/status-badge/EditStatusBadge'),
  loading: LoadingFallback
});
const StatusBannerView = loadable({
  loader: () => import('./screens/status-embed/alert-widget/EditAlertWidget'),
  loading: LoadingFallback
});
const ServiceCustomization = loadable({
  loader: () => import('./screens/service-customize'),
  loading: LoadingFallback
});
const BasicCustomization = loadable({
  loader: () => import('./screens/statuspage-customize/basic'),
  loading: LoadingFallback
});
const AdvancedCustomization = loadable({
  loader: () => import('./screens/statuspage-customize/advanced'),
  loading: LoadingFallback
});
const PrivacyCustomization = loadable({
  loader: () => import('./screens/statuspage-customize/privacy'),
  loading: LoadingFallback
});

/**
 * Settings sub routes
 */

const SettingsList = loadable({
  loader: () => import('./screens/settings/SettingsList'),
  loading: LoadingFallback
});

const AccountSettings = loadable({
  loader: () => import('./screens/settings/screens/Account'),
  loading: LoadingFallback
});
const UsersSettings = loadable({
  loader: () => import('./screens/settings/screens/Users'),
  loading: LoadingFallback
});
const InternalNotification = loadable({
  loader: () => import('./screens/settings/screens/Team'),
  loading: LoadingFallback
});
const SubscribersSettings = loadable({
  loader: () => import('./screens/settings/screens/ManageSubscribers'),
  loading: LoadingFallback
});
const IntegrationSettings = loadable({
  loader: () => import('./screens/settings/screens/Integrations'),
  loading: LoadingFallback
});
const PlansSettings = loadable({
  loader: () => import('./screens/settings/screens/Plans'),
  loading: LoadingFallback
});
const TemplatesSettings = loadable({
  loader: () => import('./screens/settings/screens/Templates'),
  loading: LoadingFallback
});
const TemplateCreation = loadable({
  loader: () => import('./screens/settings/screens/Templates/TemplateCreation'),
  loading: LoadingFallback
});

const ServiceHistoryList = () => <h1>Service status history!</h1>;

/**
 * Reports sub routes
 */
const IncidentReport = loadable({
  loader: () => import('./screens/reports/screens/Incident'),
  loading: LoadingFallback
});
const MaintenanceReport = loadable({
  loader: () => import('./screens/reports/screens/Maintenance'),
  loading: LoadingFallback
});
const ComponentsReport = loadable({
  loader: () => import('./screens/reports/screens/Components'),
  loading: LoadingFallback
});

const NotFoundPage = () => <h1>not found...</h1>;

const TwitterLogInRouter = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/HandleTwitterUrlRouter.js'),
  loading: LoadingFallback
});

const TwitterLogInSuccess = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/HandleTwitterUrlParams.js'),
  loading: LoadingFallback
});

const FreshPingIntegration = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/screens/FreshPingIntegration'),
  loading: LoadingFallback
});

const FreshPingIntegrationCreateRule = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/screens/FreshPingIntegration/CreateRule'),
  loading: LoadingFallback
});

const WebhookIntegration = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/screens/WebhookIntegration'),
  loading: LoadingFallback
});

const WebhookIntegrationCreateRule = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/screens/WebhookIntegration/CreateHook'),
  loading: LoadingFallback
});

const FreshServiceIntegration = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/screens/FreshServiceIntegration'),
  loading: LoadingFallback
});

const FreshDeskIntegration = loadable({
  loader: () =>
    import('./screens/settings/screens/Integrations/screens/FreshDeskIntegration'),
  loading: LoadingFallback
});

const CannedResponsesSettings = loadable({
  loader: () => import('./screens/settings/screens/CannedResponses'),
  loading: LoadingFallback
});
const CannedResponseCreation = loadable({
  loader: () =>
    import('./screens/settings/screens/CannedResponses/CannedResponseCreation'),
  loading: LoadingFallback
});

const IncidentStatus = loadable({
  loader: () => import('./screens/settings/screens/Status'),
  loading: LoadingFallback
});

const IncidentSource = loadable({
  loader: () => import('./screens/settings/screens/Source'),
  loading: LoadingFallback
});

const SubscriptionOptions = loadable({
  loader: () =>
    import('./screens/settings/screens/ManageSubscription/screens/SubscriptionOptions'),
  loading: LoadingFallback
});

const ManageSubscription = loadable({
  loader: () => import('./screens/settings/screens/ManageSubscription'),
  loading: LoadingFallback
});

const EmailSettings = loadable({
  loader: () => import('./screens/settings/screens/EmailSettings'),
  loading: LoadingFallback
});

const EmailNotification = loadable({
  loader: () => import('./screens/settings/screens/EmailNotification'),
  loading: LoadingFallback
});

const EmailTemplate = loadable({
  loader: () =>
    import('./screens/settings/screens/EmailNotification/screens/EmailTemplate'),
  loading: LoadingFallback
});

type ErrorFallbackProps = {
  error: any
};

function ErrorFallback({ error }: ErrorFallbackProps) {
  return (
    <IsolatedContainer>
      <p>There was an error</p>
      <pre style={{ maxWidth: 700 }}>{JSON.stringify(error, null, 2)}</pre>
    </IsolatedContainer>
  );
}

const customTheme = {
  colors: {
    components: {
      tabs: {
        color: 'var(--text-disabled)',
        hoverBg: 'var(--color-smoke-50)',
        selectedBorderColor: 'var(--text-link)',
        selectedColor: 'var(--text-link)'
      }
    }
  }
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <FreemiumThemeProvider theme={customTheme}>
        <FreemiumUiProvider>
          {/* <ReactQueryDevtools /> */}
          <AppProvider>
            <AppConsumer>
              {({
                getCurrentUser,
                user,
                getCurrentUserAccounts,
                getCurrentUserAccountsMeta,
                getComponents,
                currentAccount,
                currentAccountMeta,
                statusPageUrl,
                freshIdSessionState,
                freshIdSessionToken,
                setFreshIdSession
              }) => (
                <Fragment>
                  <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <ReactQueryConfigProvider
                      config={{
                        queries: {
                          staleTime: 60 * 1000,
                          refetchOnWindowFocus: false
                        }
                      }}
                    >
                      <PosedRouter>
                        <Application
                          user={user}
                          currentAccount={currentAccount}
                          currentAccountMeta={currentAccountMeta}
                          getCurrentUser={getCurrentUser}
                          getCurrentUserAccounts={getCurrentUserAccounts}
                          getCurrentUserAccountsMeta={
                            getCurrentUserAccountsMeta
                          }
                          path="/admin"
                        >
                          <IncidentAction path="/incidents/create" />
                          <IncidentAction path="/incidents/:id/edit" />
                          <IncidentView
                            currentAccount={currentAccount}
                            path="/incidents/:type/:id"
                          />
                          <QuickStatusChange path="/quick-status" />
                          <IncidentsPage path="/incidents">
                            <IncidentsList path="/:type" />
                            {/* <AnnouncementList path="/announcement" /> */}
                            <ServiceHistoryList path="/service-history" />
                            <Redirect noThrow from="/" to="public" />
                          </IncidentsPage>
                          <ServiceCustomization
                            currentAccount={currentAccountMeta}
                            user={user}
                            path="/service-customize"
                          />
                          <StatusEmbed
                            currentAccount={currentAccountMeta}
                            user={user}
                            path="/status-embed"
                            statusPageUrl={statusPageUrl}
                          />
                          <StatusBadgeView
                            path="/status-embed/badge/:id"
                            statusPageUrl={statusPageUrl}
                          />
                          <StatusBannerView path="/status-embed/alert-widget/:id" />
                          <StatusPageCustomization
                            user={user}
                            path="/status-customize"
                          >
                            <BasicCustomization
                              path="/"
                              currentAccount={currentAccount}
                              currentAccountMeta={currentAccountMeta}
                              getCurrentUserAccounts={getCurrentUserAccounts}
                              getCurrentUserAccountsMeta={
                                getCurrentUserAccountsMeta
                              }
                              user={user}
                            />
                            <AdvancedCustomization
                              path="/advanced"
                              currentAccount={currentAccount}
                              currentAccountMeta={currentAccountMeta}
                              getCurrentUserAccounts={getCurrentUserAccounts}
                              getCurrentUserAccountsMeta={
                                getCurrentUserAccountsMeta
                              }
                            />
                          </StatusPageCustomization>
                          {appConfig.ALERTS && (
                            <AlertsPage user={user} path="/alerts" />
                          )}
                          <SettingsPage user={user} path="/settings">
                            <SettingsList path="/" />
                            <AccountSettings path="/accounts" />
                            <UsersSettings path="/users" />
                            <SubscribersSettings path="/subscribers" />
                            <IntegrationSettings path="/integration" />
                            {appConfig.FRESH_SERVICE_INTEGRATION && (
                              <FreshServiceIntegration path="/integration/freshservice" />
                            )}
                            {appConfig.FRESH_DESK_INTEGRATION && (
                              <FreshDeskIntegration path="/integration/freshdesk" />
                            )}
                            {appConfig.FRESH_PING_INTEGRATION && (
                              <>
                                <FreshPingIntegration path="/integration/freshping" />
                                <FreshPingIntegrationCreateRule path="/integration/freshping/create" />
                                <FreshPingIntegrationCreateRule path="/integration/freshping/:id/edit" />
                              </>
                            )}
                            {appConfig.WEBHOOK_INTEGRATION && (
                              <>
                                <WebhookIntegration path="/integration/webhook" />
                                <WebhookIntegrationCreateRule path="/integration/webhook/create" />
                                <WebhookIntegrationCreateRule path="/integration/webhook/:id/edit" />
                              </>
                            )}
                            
                            <InternalNotification path="/team" />
                            {/* <PrivacySettings path="/settings/privacy" /> */}
                            <PlansSettings path="/plans" />
                            <TemplatesSettings path="/templates" />
                            <TemplateCreation path="/templates/create" />
                            <TemplateCreation path="/templates/:id/edit" />
                            <CannedResponsesSettings path="/cannedresponses" />
                            <CannedResponseCreation path="/cannedresponses/create" />
                            <CannedResponseCreation path="/cannedresponses/:id/edit" />
                            <IncidentStatus path="/incident-status" />
                            <IncidentSource path="/source" />
                            <ManageSubscription path="/manage-subscription">
                              <PrivacyCustomization path="/privacy" />
                              <SubscriptionOptions path="/subscription-options" />
                            </ManageSubscription>
                            <EmailSettings path="/email-settings" />
                            <EmailNotification path="/email-notification" />
                            <EmailTemplate
                              user={user}
                              path="/email-notification/:type/:nId/create"
                            />
                            <EmailTemplate
                              user={user}
                              path="/email-notification/:type/:nId/:cId/edit"
                            />
                          </SettingsPage>
                          <ReportsPage user={user} path="/reports">
                            <IncidentReport path="/incident" />
                            <MaintenanceReport path="/maintenance" />
                            <ComponentsReport path="/components" />
                            <Redirect noThrow from="/" to="incident" />
                          </ReportsPage>
                          <ExplorePage
                            user={user}
                            getCurrentUser={getCurrentUser}
                            currentAccount={currentAccount}
                            path="/explore"
                          />
                        </Application>

                        <FreshidSignupSuccess
                          path="/admin/token-login/:auth_token/:account_id/"
                          getCurrentUserAccounts={getCurrentUserAccounts}
                          getCurrentUserAccountsMeta={
                            getCurrentUserAccountsMeta
                          }
                          getComponents={getComponents}
                        />
                        <FreshidSSOSuccess
                          path="/admin/freshid/oauth/success"
                          setFreshIdSession={setFreshIdSession}
                        />
                        <FreshidLogout path="/admin/freshid/logout" />
                        <FreshstatusLogout path="/admin/client-logout/" />
                        <FreshidLogin path="/admin" />
                        <FreshidLogin path="/admin/login" />
                        {/* <IntegrationSettings path="/" /> */}
                        <TwitterLogInRouter path="/admin/twitter/oauth/success" />
                        <TwitterLogInSuccess path="/admin/twitter/success" />
                        <NotFoundPage path="*" />
                      </PosedRouter>
                    </ReactQueryConfigProvider>
                    <ToastContainer autoClose={5000} hideProgressBar />
                    {currentAccount && freshIdSessionState && freshIdSessionToken && (
                      <freshworks-omnibar
                        data-omnibar-product={appConfig.APP}
                        data-omnibar-environment={appConfig.ENV}
                        data-omnibar-user-role={appConfig.OB_ROLE}
                        data-omnibar-user-locale="en-us"
                        data-omnibar-organisation-domain={currentAccount.freshid_organization}
                        data-omnibar-client-id={appConfig.clientId}
                        data-omnibar-session-state={freshIdSessionState}
                        data-omnibar-session-token={freshIdSessionToken}
                      />
                    )}
                  </ErrorBoundary>
                  {/* TO-DO - Remove once v2 changes come in */}
                  <GlobalResetCSS />
                  {/* <ReactQueryDevtools /> */}
                </Fragment>
              )}
            </AppConsumer>
          </AppProvider>
        </FreemiumUiProvider>
      </FreemiumThemeProvider>
    </ThemeProvider>
  );
}

const ui = <App />;
const container = document.getElementById('root');

ReactDOM.render(ui, container);

// enable hot module replacement
if (module.hot) {
  module.hot.accept('./shared/Application', () => {
    const NextApp = <App />;
    ReactDOM.render(NextApp, container);
  });
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
// serviceWorker.unregister();
